import React from 'react';
import { Link } from 'react-router-dom';

import { Button, Input, Spin, Tooltip, Typography } from 'antd';

import CustomCopyToClipboard from 'components/CustomCopyToClipboard';
import { functions } from 'helpers';
import { constants } from 'helpers/constants';
import { EChargeType } from 'interfaces/IEditProduct';
import { HOST_ORBITA, URL_SELECT, VERTICAL_URL } from 'settings';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import ActionCollapse from 'v2/components/Shared/QuickView/ActionCollapse';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import { Icon } from '@eduzz/ui-antd-theme';

import { ProductChangeHistory } from './ProductChangeHistory';
import QuickViewLinks from './QuickviewLinks';

type Props = {
  statusProduct: 'active' | 'inactive' | 'filed' | 'draft' | ' ';
  productDataReceive: any;
  physicalProductPending: boolean;
  copiedCheckout: () => void;
  handleChange: (props: any, event: any) => void;
  inputInviteAffiliate: string;
  productData: any;
  isValid: (field: any) => boolean;
  handleClickInviteAffiliate: () => void;
  isInvitingAffiliate: boolean;
  errorFor: (field: any) => any;
  handleClickGetAffiliatesProductStatusSummary: () => void;
  affiliatesStatusSummary: any;
  isLoadingAffiliatesSummary: boolean;
  approvedAffiliatesAmount: number;
  disapprovedAffiliatesAmount: number;
  pendingAffiliatesAmount: number;
  cantManualDelivery: any;
  inputDeliveryError: boolean;
  inputDelivery: string;
  handleClickDelivery: () => void;
  isDelivering: boolean;
  handleClickGetInvoice: () => void;
  productInvoice: any;
  isLoadingInvoice: boolean;
};

const ActionSuffix = ({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) => (
  <span
    onClick={onClick}
    className={`ml-2 inline-flex items-center border-l border-(--uizz-antd-color-border-secondary) pl-2 ${
      onClick ? 'cursor-pointer' : 'cursor-default'
    }`}
  >
    {children}
  </span>
);

const actionLinkClassName =
  'flex h-12.5 w-full items-center justify-between gap-2 rounded-(--uizz-antd-border-radius) border border-(--uizz-antd-color-border-secondary) px-4 py-3 text-left text-(--uizz-antd-color-text) no-underline hover:bg-(--uizz-antd-color-fill-tertiary) hover:text-(--uizz-antd-color-text)';

const QuickActions = ({
  productDataReceive,
  statusProduct,
  physicalProductPending,
  copiedCheckout,
  handleChange,
  inputInviteAffiliate,
  productData,
  isValid,
  handleClickInviteAffiliate,
  isInvitingAffiliate,
  errorFor,
  handleClickGetAffiliatesProductStatusSummary,
  affiliatesStatusSummary: _affiliatesStatusSummary,
  isLoadingAffiliatesSummary,
  approvedAffiliatesAmount,
  disapprovedAffiliatesAmount,
  pendingAffiliatesAmount,
  cantManualDelivery,
  inputDeliveryError,
  inputDelivery,
  handleClickDelivery,
  isDelivering,
  handleClickGetInvoice,
  productInvoice,
  isLoadingInvoice
}: Props) => {
  const { id, has_children, parent_product, charge_type, type, is_highticket } = productDataReceive;

  const isBetaBatchCoupon = eduzzAccountsService.verifyUserHasBetaModule('cupom_lote');
  const hasParentProduct = parent_product !== 0;
  const oldUrl = `/producer/coupons/${hasParentProduct ? `${parent_product}/` : ''}${id}`;
  const verticalCouponUrl = `${VERTICAL_URL}/coupons?productId=${id}`;
  const couponURL = !isBetaBatchCoupon ? verticalCouponUrl : oldUrl;
  const isAffiliateBlocked = !has_children && charge_type === EChargeType.FREE;

  if (statusProduct === 'draft') {
    return (
      <div className='flex flex-col gap-2'>
        <Typography.Text type='secondary'>Ações Rápidas</Typography.Text>
        <ActionCollapse title='Links' icon={<Icon.Link size={16} />}>
          <div className='mb-1 flex items-center gap-2'>
            <Icon.Info size={16} />
            <Typography.Text strong>Finalize o cadastro do seu produto.</Typography.Text>
          </div>
          <Typography.Paragraph type='secondary'>
            Falta pouco para você começar a vender na Eduzz, clique em editar e finalize o cadastro desse produto para
            habilitar o link de venda.
          </Typography.Paragraph>
        </ActionCollapse>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      <Typography.Text type='secondary'>Ações Rápidas</Typography.Text>

      <ProductChangeHistory productData={productDataReceive} />

      {!has_children &&
        ((statusProduct && statusProduct === 'active') || statusProduct === 'inactive') &&
        !physicalProductPending && <QuickViewLinks copiedCheckout={copiedCheckout} product={productDataReceive} />}

      {!is_highticket &&
        (has_children || (!has_children && parent_product === 0)) &&
        ((statusProduct && statusProduct === 'active') || statusProduct === 'inactive') && (
          <Tooltip
            placement='top'
            title={isAffiliateBlocked ? 'O recurso de afiliado está indisponível para produtos gratuitos' : undefined}
          >
            <div>
              <ActionCollapse
                title='Adicionar Afiliados'
                icon={<Icon.UserPlus size={16} />}
                disabled={isAffiliateBlocked}
              >
                <CustomCopyToClipboard
                  id='invite-affiliate-link'
                  text={`${HOST_ORBITA}/affiliate/invite/${id}`}
                  disableUnderline={true}
                  fullWidth
                />

                <div className='mt-2'>
                  <Typography.Text id='invite-email-label' className='mb-1 block'>
                    Convide por E-mail
                  </Typography.Text>
                  <Input
                    id='invite-email'
                    aria-labelledby='invite-email-label'
                    type='text'
                    placeholder='E-mail do afiliado'
                    onChange={e => handleChange('inputInviteAffiliate', e)}
                    value={inputInviteAffiliate}
                    autoFocus
                    status={isValid('inputInviteAffiliate') ? 'error' : undefined}
                    suffix={
                      <Tooltip title='Enviar'>
                        <ActionSuffix onClick={() => handleClickInviteAffiliate()}>
                          {isInvitingAffiliate ? <Spin size='small' /> : <Icon.Send size={16} />}
                        </ActionSuffix>
                      </Tooltip>
                    }
                  />
                  {!isValid('inputInviteAffiliate') ? null : (
                    <Typography.Text type='danger'>{errorFor('inputInviteAffiliate')}</Typography.Text>
                  )}
                </div>
              </ActionCollapse>
            </div>
          </Tooltip>
        )}

      {!is_highticket &&
        ((statusProduct && statusProduct === 'active') || statusProduct === 'inactive' || statusProduct === 'filed') &&
        !productData.parent_product && (
          <ActionCollapse
            title='Ver Afiliados do Produto'
            icon={<Icon.Users size={16} />}
            onExpand={expanded => {
              if (expanded) handleClickGetAffiliatesProductStatusSummary();
            }}
          >
            <div className='flex flex-col gap-4'>
              {isLoadingAffiliatesSummary && (
                <div className='py-2.5 text-center'>
                  <Spin />
                </div>
              )}

              {!isLoadingAffiliatesSummary && (
                <table className='w-full'>
                  <thead>
                    <tr>
                      <th className='w-1/3 border-none p-0 text-left font-normal text-(--uizz-antd-color-text-secondary)'>
                        Aprovados
                      </th>
                      <th className='w-1/3 border-none p-0 text-left font-normal text-(--uizz-antd-color-text-secondary)'>
                        Reprovados
                      </th>
                      <th className='w-1/3 border-none p-0 text-left font-normal text-(--uizz-antd-color-text-secondary)'>
                        Pendentes
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className='w-1/3 border-none p-0 text-left text-sm font-semibold text-(--uizz-antd-color-text)'>
                        {approvedAffiliatesAmount}
                      </td>
                      <td className='w-1/3 border-none p-0 text-left text-sm font-semibold text-(--uizz-antd-color-text)'>
                        {disapprovedAffiliatesAmount}
                      </td>
                      <td className='w-1/3 border-none p-0 text-left text-sm font-semibold text-(--uizz-antd-color-text)'>
                        {pendingAffiliatesAmount}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
              <Link to={`/producer/myaffiliates?p=${id}&status=approved`}>Ver todos os afiliados</Link>
            </div>
          </ActionCollapse>
        )}

      <Tooltip
        placement='top'
        title={
          cantManualDelivery
            ? 'Entrega manual não é permitida para esse produto. Ou não possui entrega online cadastrada'
            : undefined
        }
      >
        <div>
          <ActionCollapse title='Entrega Manual' icon={<Icon.Send size={16} />} disabled={cantManualDelivery}>
            <Typography.Text id='delivery-manual-label' className='mb-1 block'>
              E-mail do Cliente
            </Typography.Text>
            <Input
              id='delivery-manual'
              aria-labelledby='delivery-manual-label'
              type='email'
              onChange={e => handleChange('inputDelivery', e)}
              status={inputDeliveryError || isValid('inputDelivery') ? 'error' : undefined}
              value={inputDelivery}
              autoFocus
              suffix={
                <>
                  {!isDelivering && !inputDeliveryError && (
                    <Tooltip title='Enviar'>
                      <ActionSuffix onClick={handleClickDelivery}>
                        <Icon.Send size={16} />
                      </ActionSuffix>
                    </Tooltip>
                  )}
                  {!isDelivering && inputDeliveryError && (
                    <Tooltip title='Erro ao enviar'>
                      <ActionSuffix>
                        <Typography.Text type='danger'>✕</Typography.Text>
                      </ActionSuffix>
                    </Tooltip>
                  )}
                  {isDelivering && !inputDeliveryError && (
                    <ActionSuffix>
                      <Spin size='small' />
                    </ActionSuffix>
                  )}
                </>
              }
            />
            {isValid('inputDelivery') && <Typography.Text type='danger'>{errorFor('inputDelivery')}</Typography.Text>}
          </ActionCollapse>
        </div>
      </Tooltip>

      {((statusProduct && statusProduct === 'active') || statusProduct === 'inactive' || statusProduct === 'filed') && (
        <ActionCollapse
          title='Estatísticas'
          icon={<Icon.FileText size={16} />}
          onExpand={expanded => {
            if (expanded) handleClickGetInvoice();
          }}
        >
          {isLoadingInvoice && (
            <div className='py-2.5 text-center'>
              <Spin />
            </div>
          )}

          {!isLoadingInvoice && (
            <table className='w-full'>
              <thead>
                <tr>
                  <th className='w-1/4 border-none p-0 text-center font-normal text-(--uizz-antd-color-text-secondary)'>
                    Abertas
                  </th>
                  <th className='w-1/4 border-none p-0 text-center font-normal text-(--uizz-antd-color-text-secondary)'>
                    Canceladas
                  </th>
                  <th className='w-1/4 border-none p-0 text-center font-normal text-(--uizz-antd-color-text-secondary)'>
                    Pagas
                  </th>
                  <th className='w-1/4 border-none p-0 text-center font-normal text-(--uizz-antd-color-text-secondary)'>
                    Reembolsadas
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className='w-1/4 border-none p-0 text-center text-sm'>{productInvoice?.open}</td>
                  <td className='w-1/4 border-none p-0 text-center text-sm'>{productInvoice?.canceled}</td>
                  <td className='w-1/4 border-none p-0 text-center text-sm font-semibold text-(--uizz-antd-color-success)'>
                    {productInvoice?.paid}
                  </td>
                  <td className='w-1/4 border-none p-0 text-center text-sm'>{productInvoice?.refunded}</td>
                </tr>
              </tbody>
            </table>
          )}

          {is_highticket && (statusProduct === 'active' || statusProduct === 'inactive') && (
            <div className='mt-3'>
              <Typography.Link
                id='quickview-highticket-recovery-messages-link'
                href={`/producer/rvpmessages/${id}`}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center gap-1 text-sm'
              >
                Mensagens de recuperação
                <Icon.ExternalLink size={14} />
              </Typography.Link>
            </div>
          )}
        </ActionCollapse>
      )}

      {!is_highticket &&
        ((!has_children && statusProduct && statusProduct === 'active') || statusProduct === 'inactive') &&
        !(charge_type === 'A' && type === constants.PHYSICAL) &&
        charge_type !== 'L' && (
          <PermissionHide action='myeduzz:meus-produtos:gerenciar-cupons'>
            {!isBetaBatchCoupon ? (
              <Typography.Link id='quickview-coupons-link' href={couponURL} className={actionLinkClassName}>
                <span className='flex min-w-0 items-center gap-2'>
                  <Icon.CircleDollarSign size={16} className='shrink-0 text-(--uizz-antd-color-text-secondary)' />
                  <Typography.Text ellipsis>Cupons</Typography.Text>
                </span>
                <Icon.ChevronRight size={16} className='shrink-0 text-(--uizz-antd-color-text-secondary)' />
              </Typography.Link>
            ) : (
              <Link
                id='quickview-coupons-link'
                to={couponURL}
                onClick={functions.addScrollPage}
                className={actionLinkClassName}
              >
                <span className='flex min-w-0 items-center gap-2'>
                  <Icon.DollarSign size={16} className='shrink-0 text-(--uizz-antd-color-text-secondary)' />
                  <Typography.Text ellipsis>Cupons</Typography.Text>
                </span>
                <Icon.ChevronRight size={16} className='shrink-0 text-(--uizz-antd-color-text-secondary)' />
              </Link>
            )}
          </PermissionHide>
        )}

      {!is_highticket &&
        ((!has_children && statusProduct && statusProduct === 'active') || statusProduct === 'inactive') && (
          <Link
            id='quickview-recovery-messages-link'
            to={`/producer/rvpmessages/${id}`}
            onClick={functions.addScrollPage}
            className={actionLinkClassName}
          >
            <span className='flex min-w-0 items-center gap-2'>
              <Icon.MessageSquare size={16} className='shrink-0 text-(--uizz-antd-color-text-secondary)' />
              <Typography.Text ellipsis>Mensagens de Recuperação</Typography.Text>
            </span>
            <Icon.ChevronRight size={16} className='shrink-0 text-(--uizz-antd-color-text-secondary)' />
          </Link>
        )}

      {!is_highticket && (
        <Button
          id='quickview-showcase-link'
          type='text'
          block
          onClick={() => window.open(`${URL_SELECT}/product/details/${id}/source`, '_blank')}
          className={actionLinkClassName}
        >
          <span className='flex min-w-0 items-center gap-2'>
            <Icon.List size={16} className='shrink-0 text-(--uizz-antd-color-text-secondary)' />
            <Typography.Text ellipsis>Vitrine</Typography.Text>
          </span>
          <Icon.ChevronRight size={16} className='shrink-0 text-(--uizz-antd-color-text-secondary)' />
        </Button>
      )}
    </div>
  );
};

export default QuickActions;
